import { useCallback } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

export function useImagePaste(getOrgHeaders) {
  const uploadImage = useCallback(async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    try {
      // DO NOT set Content-Type — axios auto-sets it with multipart boundary
      const { data } = await axios.post(`${API}/api/upload`, formData, {
        withCredentials: true,
        headers: getOrgHeaders(),
      });
      return { url: `${API}${data.url}` };
    } catch (err) {
      const detail = err.response?.data?.detail || err.message || 'Upload failed';
      console.error('Upload failed:', detail, err);
      return { error: detail };
    }
  }, [getOrgHeaders]);

  const handlePaste = useCallback(async (e, currentVal, setVal) => {
    const items = e.clipboardData?.items;
    if (!items) return;
    for (const item of items) {
      if (item.type.startsWith('image/')) {
        e.preventDefault();
        const file = item.getAsFile();
        if (!file) continue;
        const placeholder = '\n![Uploading image...]()\n';
        setVal(prev => (prev || '') + placeholder);
        const result = await uploadImage(file);
        if (result.url) {
          setVal(prev => prev.replace(placeholder, `\n![pasted image](${result.url})\n`));
        } else {
          setVal(prev => prev.replace(placeholder, `\n[Image upload failed: ${result.error}]\n`));
        }
        return;
      }
    }
  }, [uploadImage]);

  // File picker handler — opens native file dialog, uploads, and inserts markdown
  const handleFileUpload = useCallback(async (setVal) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.onchange = async (e) => {
      const file = e.target.files?.[0];
      if (!file) return;
      const placeholder = `\n![Uploading ${file.name}...]()\n`;
      setVal(prev => (prev || '') + placeholder);
      const result = await uploadImage(file);
      if (result.url) {
        setVal(prev => prev.replace(placeholder, `\n![${file.name}](${result.url})\n`));
      } else {
        setVal(prev => prev.replace(placeholder, `\n[Upload failed: ${result.error}]\n`));
      }
    };
    input.click();
  }, [uploadImage]);

  return { handlePaste, uploadImage, handleFileUpload };
}
